import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import verifiedLogo from "../assets/images/svg/verifiedlogo.svg";

import {
  ChevronLeft,
  BadgeCheck,
  Building2,
  Check,
  Code2,
  ExternalLink,
  Globe2,
  Link2,
  Monitor,
} from "lucide-react";

export type VerificationResultData = {
  domain: string;
  websiteUrl: string;
  logoUrl?: string;

  verificationMethod: string;
  verifiedOn: string;
  renewedOn: string;
  expiryDate: string;
  category: string;

  organization: string;
  contactEmail: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;

  sslStatus: string;
  sslCertificate: string;
  hostingProvider: string;
  serverLocation: string;
  ipAddress: string;
  nameservers: string;

  registrarName: string;
  ianaId: string;
  abuseEmail: string;
  abusePhone: string;
  registrarWebsite: string;
};

type VerificationResultProps = {
  /** Pre-shaped data (demo path). */
  data?: VerificationResultData;
  /** Raw API response (verify path). Adapted internally. */
  result?: unknown;
  onBack?: () => void;
  /** Triggered from the demo path. */
  onVerifyAnother?: () => void;
  /** Triggered from the API path — treated as an alias of onVerifyAnother. */
  onNewVerification?: () => void;
};

type DetailRow = {
  label: string;
  value: ReactNode;
};

type DetailCardProps = {
  title: string;
  icon: LucideIcon;
  rows: DetailRow[];
};

// 1. BOOKING.COM DEMO (Original)
export const sampleVerificationResult: VerificationResultData = {
  domain: "booking.com",
  websiteUrl: "https://www.booking.com/",
  verificationMethod: "WHOIS Match + SSL Check + Manual Review",
  verifiedOn: "02 May 2026, 15:46",
  renewedOn: "15 May 2026, 18:40",
  expiryDate: "15 May 2027, 23:59",
  category: "Travel Booking / Hospitality",
  organization: "Booking.com B.V.",
  contactEmail: "customer.service@booking.com",
  addressLine1: "Herengracht 597",
  addressLine2: "Rembrandt Square Office (optional)",
  city: "Amsterdam",
  state: "North Holland",
  postalCode: "1017 CE",
  country: "Netherlands",
  sslStatus: "Valid",
  sslCertificate: "DigiCert TLS RSA SHA256",
  hostingProvider: "Akamai / Booking.com Infrastructure",
  serverLocation: "Global CDN",
  ipAddress: "5.57.16.220",
  nameservers: "ns0.bkngs.com, ns1.bkngs.com",
  registrarName: "MarkMonitor Inc.",
  ianaId: "292",
  abuseEmail: "abusecomplaints@markmonitor.com",
  abusePhone: "+1.2086851750",
  registrarWebsite: "markmonitor.com",
};

// 2. EXPEDIA DEMO
export const expediaVerificationResult: VerificationResultData = {
  domain: "expedia.co.in",
  websiteUrl: "https://www.expedia.co.in/",
  verificationMethod: "WHOIS Match + SSL Check + Manual Review",
  verifiedOn: "14 Aug 2026, 10:15",
  renewedOn: "14 Aug 2026, 10:15",
  expiryDate: "14 Aug 2027, 23:59",
  category: "Travel Booking / Travel Agency",
  organization: "Expedia, Inc.",
  contactEmail: "hostmaster@expedia.com",
  addressLine1: "1111 Expedia Group Way W",
  addressLine2: "",
  city: "Seattle",
  state: "Washington",
  postalCode: "98119",
  country: "United States",
  sslStatus: "Valid",
  sslCertificate: "DigiCert Global G2 TLS RSA SHA256",
  hostingProvider: "Amazon Web Services",
  serverLocation: "US West / Global CDN",
  ipAddress: "52.38.11.12",
  nameservers: "ns1.expedns.com, ns2.expedns.com",
  registrarName: "MarkMonitor Inc.",
  ianaId: "292",
  abuseEmail: "abusecomplaints@markmonitor.com",
  abusePhone: "+1.2086851750",
  registrarWebsite: "markmonitor.com",
};

// 3. AIRBNB DEMO
export const airbnbVerificationResult: VerificationResultData = {
  domain: "airbnb.co.in",
  websiteUrl: "https://www.airbnb.co.in/",
  verificationMethod: "WHOIS Match + SSL Check + Manual Review",
  verifiedOn: "05 Sep 2026, 14:20",
  renewedOn: "05 Sep 2026, 14:20",
  expiryDate: "05 Sep 2027, 23:59",
  category: "Travel / Vacation Rentals",
  organization: "Airbnb, Inc.",
  contactEmail: "domains@airbnb.com",
  addressLine1: "888 Brannan Street",
  addressLine2: "",
  city: "San Francisco",
  state: "California",
  postalCode: "94103",
  country: "United States",
  sslStatus: "Valid",
  sslCertificate: "Amazon RSA 2048 M01",
  hostingProvider: "Amazon Web Services",
  serverLocation: "US East / Global CDN",
  ipAddress: "18.232.222.12",
  nameservers: "ns-1123.awsdns-12.org",
  registrarName: "MarkMonitor Inc.",
  ianaId: "292",
  abuseEmail: "abusecomplaints@markmonitor.com",
  abusePhone: "+1.2086851750",
  registrarWebsite: "markmonitor.com",
};

// 4. AGODA DEMO
export const agodaVerificationResult: VerificationResultData = {
  domain: "agoda.com",
  websiteUrl: "https://www.agoda.com/",
  verificationMethod: "WHOIS Match + SSL Check + Manual Review",
  verifiedOn: "22 Jul 2026, 09:30",
  renewedOn: "22 Jul 2026, 09:30",
  expiryDate: "22 Jul 2027, 23:59",
  category: "Travel Booking / Hospitality",
  organization: "Agoda Company Pte. Ltd.",
  contactEmail: "domainadmin@agoda.com",
  addressLine1: "30 Cecil Street",
  addressLine2: "#19-08 Prudential Tower",
  city: "Singapore",
  state: "Singapore",
  postalCode: "049712",
  country: "Singapore",
  sslStatus: "Valid",
  sslCertificate: "DigiCert TLS RSA SHA256",
  hostingProvider: "Agoda Network",
  serverLocation: "Global CDN",
  ipAddress: "103.111.23.45",
  nameservers: "ns1.agoda.com, ns2.agoda.com",
  registrarName: "MarkMonitor Inc.",
  ianaId: "292",
  abuseEmail: "abusecomplaints@markmonitor.com",
  abusePhone: "+1.2086851750",
  registrarWebsite: "markmonitor.com",
};

// ---------------------------------------------------------------
// Adapter: map an unknown API response into VerificationResultData
// ---------------------------------------------------------------
function mapResponseToData(
  response: unknown,
  base: VerificationResultData = sampleVerificationResult
): VerificationResultData {
  const r = (response ?? {}) as Record<string, unknown>;

  const str = (key: string, fallback = ""): string => {
    const v = r[key];
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    return fallback;
  };

  const maybeStr = (key: string): string | undefined => {
    const v = r[key];
    if (typeof v === "string" && v.length > 0) return v;
    return undefined;
  };

  return {
    domain: str("domain", str("websiteDomain", base.domain)),
    websiteUrl: str("websiteUrl", str("url", base.websiteUrl)),
    logoUrl: maybeStr("logoUrl"),

    verificationMethod: str("verificationMethod", base.verificationMethod),
    verifiedOn: str("verifiedOn", str("createdAt", base.verifiedOn)),
    renewedOn: str("renewedOn", base.renewedOn),
    expiryDate: str("expiryDate", base.expiryDate),
    category: str("category", base.category),

    organization: str("organization", str("ownerName", base.organization)),
    contactEmail: str("contactEmail", base.contactEmail),
    addressLine1: str("addressLine1", base.addressLine1),
    addressLine2: maybeStr("addressLine2"),
    city: str("city", base.city),
    state: str("state", base.state),
    postalCode: str("postalCode", base.postalCode),
    country: str("country", base.country),

    sslStatus: str("sslStatus", base.sslStatus),
    sslCertificate: str("sslCertificate", base.sslCertificate),
    hostingProvider: str("hostingProvider", base.hostingProvider),
    serverLocation: str("serverLocation", base.serverLocation),
    ipAddress: str("ipAddress", base.ipAddress),
    nameservers: str("nameservers", base.nameservers),

    registrarName: str("registrarName", base.registrarName),
    ianaId: str("ianaId", base.ianaId),
    abuseEmail: str("abuseEmail", base.abuseEmail),
    abusePhone: str("abusePhone", base.abusePhone),
    registrarWebsite: str("registrarWebsite", base.registrarWebsite),
  };
}

// Component for rendering the Logo with Text
function LogoContent() {
  return (
    <div className="flex shrink-0 items-center justify-center lg:justify-start gap-3">
      <img
        src={verifiedLogo}
        alt="VerifiedWebsite logo"
        className="h-12 w-auto object-contain md:h-14 lg:h-[60px]"
      />
      <div className="flex flex-col justify-center text-left">
        <span className="brand-name-font text-[20px] md:text-[24px] uppercase leading-[0.95] text-black lg:text-[28px]">
          Verified
        </span>
        <span className="brand-name-font text-[20px] md:text-[24px] uppercase leading-none text-black lg:text-[28px]">
          Website
        </span>
        <span className="brand-subtitle-font mt-[3px] text-[9px] md:text-[10px] uppercase leading-none text-black/60 lg:text-[12px]">
          By Vastavik
        </span>
      </div>
    </div>
  );
}

function DetailCard({ title, icon: Icon, rows }: DetailCardProps) {
  return (
    <article className="overflow-hidden rounded-[8px] border border-[#D1D1D1] bg-white w-full h-fit">
      <div className="flex items-center gap-2.5 sm:gap-3 border-b border-[#D1D1D1] px-4 py-2.5 sm:px-5 sm:py-3 bg-[#FAFAFA]">
        <Icon size={18} className="shrink-0 text-black sm:h-5 sm:w-5" />
        <h2 className="text-[15px] sm:text-[16px] font-bold font-productsans text-black leading-none mt-0.5">
          {title}
        </h2>
      </div>

      <div className="px-4 sm:px-5">
        {rows.map((row, index) => {
          const isLast = index === rows.length - 1;
          return (
            <div
              key={`${row.label}-${index}`}
              className={`flex flex-row items-center justify-between gap-3 sm:gap-6 py-[7px] sm:py-[9px]
                ${isLast ? "" : "border-b border-[#E5E5E5]"}
              `}
            >
              {/* Label — left-aligned, semibold */}
              <div className="text-left text-[12px] sm:text-[14px] font-semibold font-productsans text-black w-[45%] break-words">
                {row.label}
              </div>

              {/* Value — right-aligned, lighter color */}
              <div className="text-right text-[12px] sm:text-[14px] font-medium font-productsans text-[#666666] w-[55%] break-words">
                {row.value}
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
}

export default function VerificationResult({
  data,
  result,
  onVerifyAnother,
  onNewVerification,
}: VerificationResultProps) {
  const resolvedData: VerificationResultData =
    data ?? (result ? mapResponseToData(result) : sampleVerificationResult);

  const handleVerifyAnother = () => {
    const handler = onVerifyAnother ?? onNewVerification;
    if (handler) {
      handler();
    } else {
      window.location.href = "/";
    }
  };

  const verificationRows: DetailRow[] = [
    { label: "Website / Domain Name", value: resolvedData.domain },
    { label: "Verification Method", value: resolvedData.verificationMethod },
    { label: "Verified On", value: resolvedData.verifiedOn },
    { label: "Verification Renewed On", value: resolvedData.renewedOn },
    { label: "Verification Expiry", value: resolvedData.expiryDate },
    { label: "Category / Industry", value: resolvedData.category },
  ];

  const ownershipRows: DetailRow[] = [
    { label: "Owner / Organization", value: resolvedData.organization },
    { label: "Contact Email", value: resolvedData.contactEmail },
    { label: "Address Line 1", value: resolvedData.addressLine1 },
    { label: "Address Line 2", value: resolvedData.addressLine2 || "—" },
    { label: "City", value: resolvedData.city },
    { label: "State", value: resolvedData.state },
    { label: "PIN Code", value: resolvedData.postalCode },
    { label: "Country", value: resolvedData.country },
  ];

  const technicalRows: DetailRow[] = [
    {
      label: "SSL Certificate Status",
      value: (
        <>
          <span className="font-black text-black">{resolvedData.sslStatus}</span>
          <span> · {resolvedData.sslCertificate}</span>
        </>
      ),
    },
    { label: "Hosting Provider", value: resolvedData.hostingProvider },
    { label: "Server Location", value: resolvedData.serverLocation },
    { label: "IP Address", value: resolvedData.ipAddress },
    { label: "Nameservers", value: resolvedData.nameservers },
  ];

  const registrarRows: DetailRow[] = [
    { label: "Registrar Name", value: resolvedData.registrarName },
    { label: "IANA ID", value: resolvedData.ianaId },
    { label: "Abuse Contact Email", value: resolvedData.abuseEmail },
    { label: "Abuse Contact Phone", value: resolvedData.abusePhone },
    { label: "About the Registrar", value: resolvedData.registrarWebsite },
  ];

  const linkMatchRows: DetailRow[] = [
    { label: "Clicked Link", value: resolvedData.websiteUrl },
    {
      label: "Matched Domain",
      value: (
        <>
          <span className="font-black text-black">Matched</span>
          <span> · Link belongs to </span>
          <span className="font-black text-black">{resolvedData.domain}</span>
        </>
      ),
    },
  ];

  return (
    <main className="min-h-screen bg-white px-4 py-7 sm:px-7 lg:px-10 lg:py-12 flex justify-center">
      <div className="w-full max-w-[1200px]">
        
        {/* Header Section */}
        <div className="w-full mb-6 lg:mb-8 flex flex-col items-center lg:items-start">
          {/* Logo - Centered on Mobile, Left on Desktop */}
          <div className="flex justify-center lg:justify-start w-full">
            <Link to="/" className="inline-block hover:opacity-90 transition-opacity">
              <LogoContent />
            </Link>
          </div>

          {/* Back Navigation - Matches Media Component Exactly */}
          <div className="w-full flex justify-start mt-6 lg:mt-12">
            <button
              type="button"
              onClick={handleVerifyAnother}
              className="inline-flex items-center gap-1.5 font-productsans-regular text-[12px] leading-none text-[#000000] transition-opacity hover:opacity-70 sm:gap-2 sm:text-[16px]"
            >
              <ChevronLeft
                strokeWidth={2}
                className="h-[15px] w-[15px] text-[#00783F] sm:h-5 sm:w-5"
              />
              <span>Back to Verification</span>
            </button>
          </div>
        </div>

        {/* Title & Badge Section */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_327px] items-end gap-6 lg:gap-8 mb-8 lg:mb-12">
          
          {/* Main Title (Left aligned) */}
          <div className="font-productsans text-black text-left order-1">
            <h1 className="text-[32px] sm:text-[44px] lg:text-[54px] font-black leading-none tracking-tight mb-2 break-all">
              {resolvedData.domain}
            </h1>
            <h2 className="text-[18px] sm:text-[26px] lg:text-[32px] font-light leading-tight text-black/90">
              Website Verification Result
            </h2>
          </div>

          {/* Verification Summary Badge */}
          <div className="order-2 flex flex-row items-center justify-start gap-4 sm:gap-5 rounded-[8px] border border-[#A8A8A8] bg-white p-4 lg:p-5 w-full h-full min-h-[96px]">
            <div className="flex h-[52px] w-[52px] sm:h-[60px] sm:w-[60px] shrink-0 items-center justify-center rounded-full border-[2.5px] border-[#087B3E]">
              <Check size={28} strokeWidth={3} className="text-[#087B3E]" />
            </div>
            {/* Force left alignment using flex items-start and text-left */}
            <div className="flex flex-col justify-center items-start text-left">
              <h3 className="text-[15px] sm:text-[17px] font-bold font-productsans text-black leading-snug">
                Verification Summary
              </h3>
              <p className="mt-0.5 text-[12px] sm:text-[13px] font-productsans text-[#5F5F5F] leading-[1.3] text-left">
                {resolvedData.domain} <br />
                is a verified website by <br />
                <span className="uppercase text-[11px] sm:text-[12px] font-bold tracking-wider text-black">
                  Vastavik
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <section className="grid grid-cols-1 lg:grid-cols-[1fr_327px] gap-x-8 gap-y-0">
          
          {/* Left Column Data (Desktop) */}
          <div className="contents lg:flex lg:flex-col lg:gap-6">
            {/* Table: Verification Summary (Order 2 on mobile) */}
            <div className="order-2 lg:order-none mb-5 lg:mb-0">
              <DetailCard
                title="Verification Summary"
                icon={Building2}
                rows={verificationRows}
              />
            </div>

            {/* Table: Ownership Information (Order 3 on mobile) */}
            <div className="order-3 lg:order-none mb-5 lg:mb-0">
              <DetailCard
                title="Ownership Information"
                icon={Monitor}
                rows={ownershipRows}
              />
            </div>

            {/* Table: Technical Details (Order 4 on mobile) */}
            <div className="order-4 lg:order-none mb-5 lg:mb-0">
              <DetailCard
                title="Technical Details"
                icon={Code2}
                rows={technicalRows}
              />
            </div>

            {/* Table: Domain Registrar (Order 5 on mobile) */}
            <div className="order-5 lg:order-none mb-5 lg:mb-0">
              <DetailCard
                title="Domain Registrar Information"
                icon={Globe2}
                rows={registrarRows}
              />
            </div>

            {/* Table: Link match (Order 6 on mobile) */}
            <div className="order-6 lg:order-none mb-5 lg:mb-0">
              <DetailCard
                title="Link match"
                icon={Link2}
                rows={linkMatchRows}
              />
            </div>
          </div>

          {/* Right Column Data (Desktop) */}
          <div className="contents lg:flex lg:flex-col lg:gap-6">
            {/* Website Preview (Order 1 on mobile - comes immediately after Verification Summary Badge) */}
            <div className="order-1 lg:order-none mb-5 lg:mb-0">
              <article className="overflow-hidden rounded-[8px] border border-[#D1D1D1] bg-white w-full h-fit">
                <div className="flex items-center justify-between gap-3 border-b border-[#D1D1D1] px-4 py-2.5 sm:px-5 sm:py-3 bg-[#FAFAFA]">
                  <div className="flex items-center gap-2.5 sm:gap-3">
                    <Monitor size={18} className="shrink-0 text-black sm:h-5 sm:w-5" />
                    <h2 className="text-[15px] sm:text-[16px] font-bold font-productsans text-black leading-none mt-0.5">
                      Website Preview
                    </h2>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        resolvedData.websiteUrl,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-black/30"
                    aria-label={`Open ${resolvedData.domain} in a new tab`}
                    title="Open full website"
                  >
                    <ExternalLink size={16} className="sm:h-[18px] sm:w-[18px] text-[#5F5F5F]" />
                  </button>
                </div>

                <div className="p-4 sm:p-5 flex flex-col gap-4 sm:gap-5">
                  <div className="relative h-[280px] sm:h-[464px] w-full overflow-hidden rounded-[8px] bg-[#F5F5F5] border border-[#E0E0E0]">
                    <iframe
                      src={resolvedData.websiteUrl}
                      title={`Interactive preview of ${resolvedData.domain}`}
                      loading="lazy"
                      sandbox="allow-downloads allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
                      allow="clipboard-read; clipboard-write; fullscreen; payment"
                      referrerPolicy="strict-origin-when-cross-origin"
                      scrolling="yes"
                      className="h-full w-full border-0 pointer-events-none sm:pointer-events-auto"
                    />
                  </div>

                  {/* Status List */}
                  <div className="grid gap-3 text-[12px] sm:text-[14px] font-medium font-productsans text-black px-1">
                    <div className="flex items-center gap-3 break-all">
                      <Globe2 size={16} className="shrink-0 sm:h-[18px] sm:w-[18px] text-[#5F5F5F]" />
                      <span>{resolvedData.domain}</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <BadgeCheck size={16} className="shrink-0 sm:h-[18px] sm:w-[18px] text-[#5F5F5F]" />
                      <span>Verified website</span>
                    </div>

                    <div className="flex items-center gap-3 break-all">
                      <Link2 size={16} className="shrink-0 sm:h-[18px] sm:w-[18px] text-[#5F5F5F]" />
                      <span>Link matches {resolvedData.domain}</span>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Bottom Verify Button */}
        <div className="mt-4 lg:mt-12 flex justify-center pb-8 sm:pb-12">
          <button
            type="button"
            onClick={handleVerifyAnother}
            className="flex h-[46px] sm:h-auto w-full sm:w-auto sm:min-w-[300px] items-center justify-center rounded-[8px] bg-black px-6 sm:px-10 py-0 sm:py-4 text-[16px] sm:text-[18px] font-productsans font-medium text-white transition hover:bg-black/80"
          >
            Verify Another Website
          </button>
        </div>
      </div>
    </main>
  );
}